import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Default axios instance — credentials OFF so public CORS (Access-Control-
// Allow-Origin: *) works in cross-origin deployments. Admin/auth routes pass
// `WITH_CREDS` explicitly to opt in to the httpOnly JWT cookie.
export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});
const WITH_CREDS = { withCredentials: true };

/* ─── Admin / auth ─── */
export async function authLogin(email, password) {
  const { data } = await api.post("/auth/login", { email, password }, WITH_CREDS);
  return data;
}

export async function authLogout() {
  await api.post("/auth/logout", null, WITH_CREDS);
}

export async function authMe() {
  const { data } = await api.get("/auth/me", WITH_CREDS);
  return data;
}

export async function fetchAdminOrders(params = {}) {
  const { data } = await api.get("/admin/orders", { params, ...WITH_CREDS });
  return data;
}

export async function fetchAdminDashboard() {
  const { data } = await api.get("/admin/dashboard", WITH_CREDS);
  return data;
}

export async function patchOrderStatus(orderId, status) {
  const { data } = await api.patch(`/admin/orders/${orderId}/status`, { status }, WITH_CREDS);
  return data;
}

export function adminOrdersCsvUrl(params = {}) {
  // Build a same-origin URL the browser can hit directly (cookie auth).
  const qs = new URLSearchParams(params).toString();
  return `${API}/admin/orders.csv${qs ? `?${qs}` : ""}`;
}

export async function retryEasyshipShipment(orderId) {
  const { data } = await api.post(`/admin/orders/${orderId}/shipment/retry`, null, WITH_CREDS);
  return data;
}

export async function editOrder(orderId, updates) {
  const { data } = await api.patch(`/admin/orders/${orderId}`, updates, WITH_CREDS);
  return data;
}

export async function createCheckoutSession(items) {
  const payload = {
    items: items.map((i) => ({ product_id: i.id, quantity: i.quantity, size: i.size || null })),
    origin_url: window.location.origin,
  };
  const { data } = await api.post("/checkout/session", payload);
  return data;
}

/* Custom checkout: persists a full Order (customer + addresses) and returns
 * the Stripe session URL to redirect to. */
export async function createOrder({ items, customer, shipping_address, billing_address, notes, discount_code }) {
  const payload = {
    items: items.map((i) => ({ product_id: i.id, quantity: i.quantity, size: i.size || null })),
    customer,
    shipping_address,
    billing_address: billing_address || null,
    notes: notes || null,
    discount_code: discount_code || null,
    origin_url: window.location.origin,
  };
  const { data } = await api.post("/orders", payload);
  return data;
}

export async function validateDiscount(code, subtotal, shipping_fee = 0) {
  const { data } = await api.post("/discount/validate", {
    code, subtotal, shipping_fee,
  });
  return data;
}

export async function getOrder(orderId) {
  const { data } = await api.get(`/orders/${orderId}`);
  return data;
}

export async function getCheckoutStatus(sessionId) {
  const { data } = await api.get(`/checkout/status/${sessionId}`);
  return data;
}

/* ─── Customer account ─── */
export async function customerSignup({ email, password, first_name, last_name }) {
  const { data } = await api.post("/account/signup",
    { email, password, first_name, last_name }, WITH_CREDS);
  return data;
}
export async function customerLogin(email, password) {
  const { data } = await api.post("/account/login", { email, password }, WITH_CREDS);
  return data;
}
export async function customerLogout() {
  await api.post("/account/logout", null, WITH_CREDS);
}
export async function customerMe() {
  const { data } = await api.get("/account/me", WITH_CREDS);
  return data;
}
export async function customerOrders() {
  const { data } = await api.get("/account/orders", WITH_CREDS);
  return data;
}
export async function customerReorder(orderId) {
  const { data } = await api.post(`/account/orders/${orderId}/reorder`, null, WITH_CREDS);
  return data;
}
export async function customerAddresses() {
  const { data } = await api.get("/account/addresses", WITH_CREDS);
  return data;
}
export async function customerAddAddress(addr) {
  const { data } = await api.post("/account/addresses", addr, WITH_CREDS);
  return data;
}
export async function customerDeleteAddress(addrId) {
  const { data } = await api.delete(`/account/addresses/${addrId}`, WITH_CREDS);
  return data;
}
export async function customerWishlist() {
  const { data } = await api.get("/account/wishlist", WITH_CREDS);
  return data;
}
export async function customerWishlistAdd(productId) {
  const { data } = await api.post(`/account/wishlist/${productId}`, null, WITH_CREDS);
  return data;
}
export async function customerWishlistRemove(productId) {
  const { data } = await api.delete(`/account/wishlist/${productId}`, WITH_CREDS);
  return data;
}
export async function customerProfileUpdate(payload) {
  const { data } = await api.patch("/account/profile", payload, WITH_CREDS);
  return data;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
}
